"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { FlowShell } from "@/components/layout/flow-shell";
import { StepCard } from "@/components/layout/step-card";
import { StepLoaderCard } from "@/components/loading";
import { DebugErrorContent } from "@/components/organisms/debug-error/debug-error-content";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";

type ErrorProps = {
	error: Error & { digest?: string };
	reset: () => void;
};

export default function Error({ error }: Readonly<ErrorProps>) {
	const router = useRouter();
	const { showErrors, errorUrl } = useRuntimeConfig();
	useEffect(() => {
		sendClientLog({
			level: "error",
			event: LogEvent.ErrorBoundaryTriggered,
			outcome: "failure",
			message: "Unhandled runtime error in root",
			error,
		});

		// Dev: keep the error on screen instead of redirecting off-site.
		if (showErrors) {
			return;
		}

		const timeout = setTimeout(() => {
			if (errorUrl) {
				router.replace(errorUrl);
			} else {
				sendClientLog({
					level: "error",
					event: LogEvent.ConfigMissing,
					outcome: "failure",
					message:
						"No error page URL configured; customer stays on the boundary",
					error,
					context: { setting: "ERROR_URL" },
				});
			}
		}, 500);

		return () => clearTimeout(timeout);
	}, [error, router, showErrors, errorUrl]);

	if (showErrors) {
		return (
			<FlowShell>
				<StepCard>
					<DebugErrorContent
						reason="Unhandled runtime error (root)"
						detail={
							error.digest
								? `${error.message}\n\ndigest: ${error.digest}`
								: error.message
						}
					/>
				</StepCard>
			</FlowShell>
		);
	}

	return (
		<FlowShell>
			<StepLoaderCard />
		</FlowShell>
	);
}
